import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/b2bDesignTokens.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ChakraProvider, ColorModeScript, extendTheme } from '@chakra-ui/react';
import { AuthProvider } from './context/AuthContext';
import { BrowserRouter } from 'react-router-dom';
import { FeatureUnlockProvider } from './context/FeatureUnlockContext';
import { JobProgressProvider } from './context/JobProgressContext';

// Clear the old color mode setting before rendering
if (localStorage.getItem('chakra-ui-color-mode')) {
  localStorage.removeItem('chakra-ui-color-mode');
}

const config = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const theme = extendTheme({
  config,
  fonts: {
    heading: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    body: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, monospace",
  },
  colors: {
    brand: {
      navy: '#0B2545',
      navy2: '#13315C',
      navyDeep: '#061A36',
      teal: '#13C1BB',
      tealSoft: '#E0F7F6',
      tealDark: '#0B6B66',
      bg: '#F5F7FA',
      sidebar: '#FAFBFD',
      sidebarHover: '#F2F4F7',
      divider: '#E5EBF2',
      ink: '#0F172A',
      muted: '#475569',
      subtle: '#64748B',
      quantumBg: '#080d19',
      quantumCard: '#111b2e',
      quantumBorder: 'rgba(255, 255, 255, 0.08)',
      quantumAccent: '#00d4aa',
      quantumCyan: '#22d3ee',
      quantumInk: 'rgba(255, 255, 255, 0.92)',
      quantumMuted: 'rgba(255, 255, 255, 0.55)',
      quantumSubtle: 'rgba(255, 255, 255, 0.35)',
    },
    blue: {
      50: '#E8EEF7',
      100: '#D7E1EF',
      200: '#B6C7DE',
      300: '#8EA8CA',
      400: '#5B7CB3',
      500: '#13315C',
      600: '#0B2545',
      700: '#061A36',
      800: '#041225',
      900: '#020A14',
    },
    teal: {
      50: '#E0F7F6',
      100: '#C3F0ED',
      200: '#90E0DC',
      300: '#5BD0CB',
      400: '#13C1BB',
      500: '#0EA6A1',
      600: '#0B6B66',
      700: '#07524E',
      800: '#063B38',
      900: '#032422',
    },
  },
  styles: {
    global: {
      html: {
        scrollBehavior: 'smooth',
        bg: 'brand.bg',
      },
      body: {
        bg: 'brand.bg',
        color: 'brand.ink',
      },
      'h1, h2, h3, h4, h5, h6': {
        letterSpacing: 0,
      },
    },
  },
  radii: {
    md: '8px',
    lg: '12px',
    xl: '14px',
    '2xl': '16px',
    '3xl': '20px',
  },
  shadows: {
    b2bCard: '0 1px 0 rgba(11, 37, 69, 0.04), 0 4px 14px rgba(11, 37, 69, 0.05)',
    b2bStrong: '0 18px 48px rgba(11, 37, 69, 0.12)',
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: '10px',
        fontWeight: 700,
        letterSpacing: 0,
      },
      variants: {
        solid: (props) => {
          if (props.colorScheme === 'blue') {
            return {
              bg: 'brand.navy',
              color: 'white',
              _hover: { bg: 'brand.navy2' },
              _active: { bg: 'brand.navyDeep' },
            };
          }
          if (props.colorScheme === 'teal') {
            return {
              bg: 'brand.teal',
              color: 'brand.navy',
              _hover: { bg: 'teal.300' },
              _active: { bg: 'teal.500' },
            };
          }
          return {};
        },
        outline: (props) => {
          if (props.colorScheme === 'blue' || props.colorScheme === 'teal') {
            return {
              borderColor: 'brand.divider',
              color: 'brand.navy',
              _hover: { bg: 'brand.tealSoft', borderColor: 'brand.teal' },
            };
          }
          return {};
        },
        ghost: {
          color: 'brand.navy',
          _hover: { bg: 'brand.sidebarHover' },
        },
      },
    },
    Badge: {
      baseStyle: {
        borderRadius: '999px',
        fontWeight: 800,
        letterSpacing: '0.08em',
      },
    },
    Card: {
      baseStyle: {
        container: {
          borderRadius: '14px',
          borderWidth: '1px',
          borderColor: 'brand.divider',
          boxShadow: 'b2bCard',
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            borderRadius: '10px',
            borderColor: 'brand.divider',
            _hover: { borderColor: 'brand.teal' },
            _focusVisible: {
              borderColor: 'brand.teal',
              boxShadow: '0 0 0 1px #13C1BB',
            },
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ChakraProvider theme={theme}>
        <ColorModeScript initialColorMode={theme.config.initialColorMode} />
        <AuthProvider>
          <FeatureUnlockProvider>
            <JobProgressProvider>
              <App />
            </JobProgressProvider>
          </FeatureUnlockProvider>
        </AuthProvider>
      </ChakraProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
